import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { defenceNotifications, contactInfo } from '../data/mock';
import { 
  Calendar, 
  Clock, 
  Users, 
  ExternalLink,
  Download,
  FileText,
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Mail
} from 'lucide-react';

const NotificationsPage = () => {
  const handleBookConsultation = () => {
    const subject = encodeURIComponent('Query Regarding Defence Exam - Jay Defence Academy');
    const body = encodeURIComponent('Hello,\n\nI would like to know more about the defence exam preparation programs.\n\nPlease contact me at your earliest convenience.\n\nThank you.');
    window.location.href = `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
  };

  // Parse date strings like "30th December 2025" or "24th February 2026"
  const parseNotificationDate = (dateStr) => {
    if (!dateStr) return null;
    
    // Remove ordinal suffixes (st, nd, rd, th)
    const cleanedDate = dateStr.replace(/(\d+)(st|nd|rd|th)/, '$1');
    const parsed = new Date(cleanedDate);
    
    // Check if valid date
    if (isNaN(parsed.getTime())) return null;
    return parsed;
  };

  // Process notifications to dynamically determine status based on lastDate
  const processedNotifications = defenceNotifications.map(notification => {
    const lastDate = parseNotificationDate(notification.lastDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time for date comparison
    
    // If lastDate has passed, mark as closed
    const dynamicStatus = lastDate && lastDate < today ? 'closed' : notification.status;
    
    return {
      ...notification,
      status: dynamicStatus
    };
  });

  // Count active notifications
  const activeCount = processedNotifications.filter(n => n.status === 'active').length;

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-16 bg-navy-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800/90 to-olive-900/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/20 rounded-full border border-gold-500/30 mb-4">
            <AlertCircle className="w-4 h-4 text-gold-400" />
            <span className="text-gold-400 text-sm font-medium">Latest Updates</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Defence Exam <span className="text-gold-400">Notifications</span>
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Stay updated with the latest defence exam notifications. Apply on time and prepare with Jay Defence Academy.
          </p>
        </div>
      </section>

      {/* Active Notifications */}
      <section className="py-16 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl font-bold text-navy-900">Active Notifications</h2>
              <p className="text-gray-600 mt-2">Currently open recruitment examinations</p>
            </div>
            <Badge className="bg-olive-600 text-white px-4 py-2">
              {activeCount} Active
            </Badge>
          </div>

          <div className="space-y-8">
            {processedNotifications.map((notification) => (
              <Card key={notification.id} className="border-0 shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300">
                <CardHeader className="bg-navy-900 text-white p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <Badge className="bg-gold-500 text-navy-900 font-semibold">
                          {notification.status === 'active' ? 'Applications Open' : 'Closed'}
                        </Badge>
                        <Badge variant="outline" className="border-white/30 text-white">
                          {notification.organization}
                        </Badge>
                      </div>
                      <CardTitle className="text-2xl font-bold">{notification.title}</CardTitle>
                      <p className="text-gray-300 mt-1">{notification.examName}</p>
                    </div>
                    <div className="flex gap-3">
                      <a href={notification.pdfUrl} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                      </a>
                      <a href={notification.officialLink} target="_blank" rel="noopener noreferrer">
                        <Button className="bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold">
                          Apply Now
                          <ExternalLink className="w-4 h-4 ml-2" />
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="p-6 bg-white">
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-navy-100 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-navy-900" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Last Date to Apply</p>
                        <p className="font-semibold text-navy-900">{notification.lastDate}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-olive-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-olive-700" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Exam Date</p>
                        <p className="font-semibold text-navy-900">{notification.examDate}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gold-100 flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-gold-700" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Vacancies</p>
                        <p className="font-semibold text-navy-900">{notification.vacancies}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-cream-200 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-navy-700" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Age Limit</p>
                        <p className="font-semibold text-navy-900">{notification.ageLimit}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6 p-4 bg-cream-50 rounded-xl">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Eligibility</p>
                      <p className="text-navy-900">{notification.eligibility}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Application Fee</p>
                      <p className="text-navy-900">{notification.applicationFee}</p>
                    </div>
                  </div>
                  
                  {/* Highlights section for SSC Tech entries */}
                  {notification.highlights && notification.highlights.length > 0 && (
                    <div className="mt-4 p-4 bg-olive-50 rounded-xl border border-olive-200">
                      <p className="text-sm font-semibold text-olive-800 mb-2">Key Highlights:</p>
                      <div className="grid md:grid-cols-2 gap-2">
                        {notification.highlights.map((highlight, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-olive-600 flex-shrink-0" />
                            <span className="text-sm text-olive-800">{highlight}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Preparation CTA */}
      <section className="py-16 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Start Your <span className="text-gold-400">Preparation</span> Today
              </h2>
              <p className="text-gray-300 text-lg mb-8">
                Don't just apply - prepare to succeed! Jay Defence Academy offers comprehensive coaching for NDA, CDS, and AFCAT examinations.
              </p>
              <div className="space-y-4">
                {[
                  'Expert guidance from retired defence officers',
                  'Complete syllabus coverage with study materials',
                  'Regular mock tests and performance analysis',
                  'Personality development and SSB preparation'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-gold-400" />
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
              <h3 className="text-xl font-bold text-white mb-6">Ready to Begin?</h3>
              <p className="text-gray-400 mb-6">
                Contact us for a free consultation and get personalized guidance for your defence career preparation.
              </p>
              <div className="space-y-4">
                <Button 
                  onClick={handleBookConsultation}
                  className="w-full bg-gold-500 hover:bg-gold-400 text-navy-900 font-bold py-6"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Book a Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Link to="/mock-exams" className="block">
                  <Button variant="outline" className="w-full border-white/30 text-white hover:bg-white/10 py-6">
                    Take a Free Mock Test
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default NotificationsPage;
